import {
	createFileRoute,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import { useEffect, useState, useRef, useTransition } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Image } from "@/components/image";
import type { launchFragment_club$key } from "./__generated__/launchFragment_club.graphql";
import type { launchOnboardingQuery } from "./__generated__/launchOnboardingQuery.graphql";
import type { ClubLaunchRefetchQuery } from "./__generated__/ClubLaunchRefetchQuery.graphql";
import { useOnboardingAnalytics } from "@/analytics/onboarding";

const loadingMessages = [
	"Preparando as salas de aula virtuais...",
	"Organizando a biblioteca digital...",
	"Ligando as luzes do campus...",
	"Atualizando o sistema de matrícula...",
	"Carregando o material didático...",
];

function RouteComponent() {
	const { club: clubSlug } = useParams({ from: Route.id });
	const { session } = useRouteContext({ from: "__root__" });
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
	const [showSuccess, setShowSuccess] = useState(false);
	const pollIntervalRef = useRef<number | null>(null);
	const launchStartTime = useRef(performance.now());
	const hasTrackedCompletion = useRef(false);
	const { capture, posthog } = useOnboardingAnalytics("launch", clubSlug);

	const data = useLazyLoadQuery<launchOnboardingQuery>(
		graphql`
      query launchOnboardingQuery($club: String!) {
        club(slug: $club) {
          ...launchFragment_club
        }
      }
    `,
		{ club: clubSlug },
	);

	const clubData = data.club;

	const [_, startTransition] = useTransition();

	const [fragmentData, refetch] = useRefetchableFragment<
		ClubLaunchRefetchQuery,
		launchFragment_club$key
	>(
		graphql`
      fragment launchFragment_club on Club
      @refetchable(queryName: "ClubLaunchRefetchQuery")
      {
        id
        slug
        logo {
          signedUrl
        }
        domains {
          enabled
          domain
        }
      }
    `,
		clubData,
	);

	const club = fragmentData;

	const clubUrl = club?.domains?.find(
		(domain: { enabled: boolean }) => domain.enabled,
	)
		? `https://${club?.domains?.find((domain: { enabled: boolean }) => domain.enabled)?.domain}`
		: undefined;

	useEffect(() => {
		if (clubUrl) {
			setShowSuccess(true);
		} else {
			setShowSuccess(false);
		}
	}, [clubUrl]);

	useEffect(() => {
		if (showSuccess && clubUrl) {
			if (!hasTrackedCompletion.current) {
				hasTrackedCompletion.current = true;
				const timeToProvision = Math.round(
					(performance.now() - launchStartTime.current) / 1000,
				);
				capture("completed", {
					domain_provisioned: true,
					domain_url: clubUrl,
					time_to_domain_seconds: timeToProvision,
				});
				posthog.capture("onboarding_completed", {
					club_slug: clubSlug,
					domain_url: clubUrl,
				});
			}

			const successTimeout = setTimeout(() => {
				const token = session?.refreshToken;
				const urlWithToken = token
					? `${clubUrl}?token=${encodeURIComponent(token)}`
					: clubUrl;
				window.location.href = urlWithToken;
			}, 5000);

			return () => clearTimeout(successTimeout);
		}
	}, [showSuccess, clubUrl, session?.refreshToken, posthog, clubSlug, capture]);

	useEffect(() => {
		const messageInterval = setInterval(
			() => {
				setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
			},
			Math.floor(Math.random() * 5000) + 5000,
		);

		pollIntervalRef.current = window.setInterval(() => {
			startTransition(() => {
				refetch({}, { fetchPolicy: "network-only" });
			});
		}, 5000);

		return () => {
			clearInterval(messageInterval);
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
			}
		};
	}, [refetch]);

	return (
		<div className="fixed inset-0 bg-background z-50 flex flex-col items-center justify-center gap-8">
			<motion.div
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5 }}
			>
				<Image
					src={club?.logo?.signedUrl ?? ""}
					className="w-32 h-32 object-contain"
					alt="Logo da comunidade"
				/>
			</motion.div>

			<div className="flex flex-col items-center gap-4">
				<AnimatePresence mode="wait">
					{showSuccess ? (
						<motion.div
							key="success"
							className="flex flex-col items-center gap-4"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.4 }}
						>
							<motion.h2
								className="text-2xl font-semibold text-foreground"
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.4 }}
							>
								✨ Seu ecossistema está pronto!
							</motion.h2>
							<motion.p
								className="text-muted-foreground text-base"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 0.4, delay: 0.2 }}
							>
								Redirecionando para sua comunidade...
							</motion.p>
						</motion.div>
					) : (
						<motion.div
							key="loading"
							className="flex flex-col items-center gap-4"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.4 }}
						>
							<motion.h2
								className="text-2xl font-semibold text-foreground"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: 0.2 }}
							>
								Configurando seu ecossistema
							</motion.h2>

							<div className="h-8 flex items-center justify-center">
								<AnimatePresence mode="wait">
									<motion.p
										key={currentMessageIndex}
										className="text-muted-foreground text-base"
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, y: -20 }}
										transition={{ duration: 0.4 }}
									>
										{loadingMessages[currentMessageIndex]}
									</motion.p>
								</AnimatePresence>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/_logged/onboarding/$club/launch")({
	component: RouteComponent,
});
